import { styleTxn } from "../../../utils/style-txn";
import { StringObject } from "../../../objects/StringObject";
import { DEVTOOLS_OFFSETS_BADGE_ICON } from "../core/StringDevtoolsIcons";
import { StringDevOverlayModule, OverlayBaseMetrics } from "../core/StringDevOverlayModule";
import { OFFSETS_CSS } from "./StringOffsets.css";


interface OffsetOverlayEntry {
  object: StringObject;
  root: HTMLDivElement;
  box: HTMLDivElement;
  topZone: HTMLDivElement;
  bottomZone: HTMLDivElement;
  topMarker: HTMLDivElement;
  bottomMarker: HTMLDivElement;
  topMarkerValue: HTMLSpanElement;
  bottomMarkerValue: HTMLSpanElement;
  badge: HTMLButtonElement;
  badgeDepth: number;
  onBadgeClick: (event: MouseEvent) => void;
}

interface OffsetOverlayMeasurement {
  visible: boolean;
  overlayVisible: boolean;
  badgeVisible: boolean;
  inview: boolean;
  boxDocX: number;
  boxDocY: number;
  width: number;
  height: number;
  borderRadius: string;
  topZoneDocX: number;
  topZoneDocY: number;
  topZoneWidth: number;
  topZoneHeight: number;
  topZoneEmpty: boolean;
  bottomZoneDocX: number;
  bottomZoneDocY: number;
  bottomZoneWidth: number;
  bottomZoneHeight: number;
  bottomZoneEmpty: boolean;
  topMarkerDocX: number;
  topMarkerDocY: number;
  topMarkerValue: string;
  bottomMarkerDocX: number;
  bottomMarkerDocY: number;
  bottomMarkerValue: string;
  badgeDocX: number;
  badgeDocY: number;
}

const OFFSET_MARKER_DOWN = `
<svg viewBox="0 0 16 16" aria-hidden="true">
  <path d="M8 2v9" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
  <path d="M4.5 8.5L8 12l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
</svg>`;

const OFFSET_MARKER_UP = `
<svg viewBox="0 0 16 16" aria-hidden="true">
  <path d="M8 14V5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
  <path d="M4.5 7.5L8 4l3.5 3.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
</svg>`;

function formatOffset(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  if (Object.is(rounded, -0)) {
    return "0";
  }
  if (Number.isInteger(rounded)) {
    return `${rounded > 0 ? "+" : ""}${rounded}`;
  }
  return `${rounded > 0 ? "+" : ""}${rounded}`;
}

function fixedTranslate(x: number, y: number): string {
  return `translate3d(${Math.round(x * 100) / 100}px, ${Math.round(y * 100) / 100}px, 0)`;
}

const MARKER_VISUAL_WIDTH = 72;
const MARKER_VISUAL_HEIGHT = 28;

function resolveOffsetBadgeDepth(element: HTMLElement): number {
  let depth = 0;
  let current = element.parentElement;

  while (current) {
    if (current.hasAttribute("string-id") || current.hasAttribute("data-string-id")) {
      depth += 1;
    }
    current = current.parentElement;
  }

  return depth;
}

export class StringDevOffsets extends StringDevOverlayModule<OffsetOverlayEntry, OffsetOverlayMeasurement> {
  static override devtool = {
    id: "offsets",
    label: "Offsets",
    icon: "offsets",
    order: 25,
    styles: OFFSETS_CSS,
    overlay: {
      layerName: "devtools-offsets",
      zIndex: 10012,
      layerAttribute: "data-string-offsets-layer",
      overlayId: "offsets",
    },
  };

  private entryEnabled: Map<string, boolean> = new Map();

  canConnect(_object: StringObject): boolean {
    return true;
  }

  protected createOverlayEntry(object: StringObject): OffsetOverlayEntry {
    this.entryEnabled.set(object.id, true);

    const targetId = this.resolveOverlayTargetId(object);
    const root = document.createElement("div");
    root.setAttribute("data-string-offsets-item", "");
    root.setAttribute("data-target-id", targetId);
    root.style.position = "absolute";
    root.style.top = "0";
    root.style.left = "0";
    root.style.overflow = "visible";
    root.style.pointerEvents = "none";

    const box = document.createElement("div");
    box.setAttribute("data-string-offsets-box", "");

    const topZone = document.createElement("div");
    topZone.setAttribute("data-string-offsets-zone", "");
    topZone.setAttribute("data-edge", "top");

    const bottomZone = document.createElement("div");
    bottomZone.setAttribute("data-string-offsets-zone", "");
    bottomZone.setAttribute("data-edge", "bottom");

    const topMarker = document.createElement("div");
    topMarker.setAttribute("data-string-offsets-marker", "");
    topMarker.setAttribute("data-edge", "top");
    topMarker.setAttribute("data-target-id", targetId);
    topMarker.innerHTML = OFFSET_MARKER_DOWN;
    const topMarkerValue = document.createElement("span");
    topMarkerValue.setAttribute("data-string-offsets-marker-value", "");
    topMarker.appendChild(topMarkerValue);

    const bottomMarker = document.createElement("div");
    bottomMarker.setAttribute("data-string-offsets-marker", "");
    bottomMarker.setAttribute("data-edge", "bottom");
    bottomMarker.setAttribute("data-target-id", targetId);
    bottomMarker.innerHTML = OFFSET_MARKER_UP;
    const bottomMarkerValue = document.createElement("span");
    bottomMarkerValue.setAttribute("data-string-offsets-marker-value", "");
    bottomMarker.appendChild(bottomMarkerValue);

    const badge = this.createOverlayBadge({
      targetId,
      selectorAttribute: "data-string-offsets-badge",
      depth: this.resolveOverlayObjectDepth(object.htmlElement),
    });
    this.applyOverlayBadgeState(badge, {
      html: DEVTOOLS_OFFSETS_BADGE_ICON,
      title: this.enabled ? "Hide offsets" : "Show offsets",
    });

    const onBadgeClick = (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      this.setEntryEnabled(object.id, !this.isEntryEnabled(object.id));
    };
    badge.addEventListener("click", onBadgeClick);

    root.appendChild(topZone);
    root.appendChild(bottomZone);
    root.appendChild(box);

    const world = this.viewportLayer.getWorldElement(this.getWorldHost());
    world.appendChild(root);
    world.appendChild(topMarker);
    world.appendChild(bottomMarker);
    world.appendChild(badge);

    return {
      object,
      root,
      box,
      topZone,
      bottomZone,
      topMarker,
      bottomMarker,
      topMarkerValue,
      bottomMarkerValue,
      badge,
      badgeDepth: resolveOffsetBadgeDepth(object.htmlElement),
      onBadgeClick,
    };
  }

  protected destroyOverlayEntry(entry: OffsetOverlayEntry): void {
    entry.badge.removeEventListener("click", entry.onBadgeClick);
    entry.root.remove();
    entry.topMarker.remove();
    entry.bottomMarker.remove();
    entry.badge.remove();
    this.entryEnabled.delete(entry.object.id);
  }

  protected measureEntry(entry: OffsetOverlayEntry, metrics: OverlayBaseMetrics): OffsetOverlayMeasurement {
    const object = entry.object;
    
    if (!metrics.visible) {
      return this.hiddenMeasurement(object);
    }

    const { contentX, contentY, width, height, baseAnchorX, baseAnchorY, collisionOffset } = metrics;
    const entryEnabled = this.isEntryEnabled(object.id);

    const borderRadius = object.getProperty<string>("layout-border-radius") ?? "0px";
    const startViewport = object.getProperty<string>("enter-vp") ?? "bottom";
    const endViewport = object.getProperty<string>("exit-vp") ?? "top";
    const startElement = object.getProperty<string>("enter-el") ?? "top";
    const endElement = object.getProperty<string>("exit-el") ?? "bottom";
    const inviewStartPosition = object.getProperty<number>("inview-start-position");
    const inviewEndPosition = object.getProperty<number>("inview-end-position");

    const topAnchor = startElement === "bottom" ? contentY + height : contentY;
    const bottomAnchor = endElement === "top" ? contentY : contentY + height;
    const topTrigger =
      inviewStartPosition != null
        ? inviewStartPosition + this.getViewportAnchorOffset(startViewport)
        : topAnchor;
    const bottomTrigger =
      inviewEndPosition != null
        ? inviewEndPosition + this.getViewportAnchorOffset(endViewport)
        : bottomAnchor;

    const topZoneStart = Math.min(topAnchor, topTrigger);
    const topZoneHeight = Math.abs(topTrigger - topAnchor);

    const bottomZoneStart = Math.min(bottomAnchor, bottomTrigger);
    const bottomZoneHeight = Math.abs(bottomTrigger - bottomAnchor);

    const markerDocX = contentX + width - 6;
    const topMarkerDocY = topTrigger;
    const bottomMarkerDocY = bottomTrigger;
    
    const badgeDocX = baseAnchorX;
    const badgeDocY = baseAnchorY + collisionOffset;

    const scrollLeft = this.getViewportScrollLeft();
    const scrollTop = this.getViewportScrollTop();
    const viewportHeight = this.data.viewport.windowHeight || window.innerHeight;
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const overlayLeft = Math.min(contentX, markerDocX - MARKER_VISUAL_WIDTH);
    const overlayRight = Math.max(contentX + width, markerDocX);
    const overlayTop = Math.min(
      contentY,
      topZoneStart,
      bottomZoneStart,
      topMarkerDocY - MARKER_VISUAL_HEIGHT,
      bottomMarkerDocY - MARKER_VISUAL_HEIGHT,
    );
    const overlayBottom = Math.max(
      contentY + height,
      topZoneStart + topZoneHeight,
      bottomZoneStart + bottomZoneHeight,
      topMarkerDocY + MARKER_VISUAL_HEIGHT,
      bottomMarkerDocY + MARKER_VISUAL_HEIGHT,
    );
    const isOffscreen =
      overlayBottom - scrollTop < 0 ||
      overlayTop - scrollTop > viewportHeight ||
      overlayRight - scrollLeft < 0 ||
      overlayLeft - scrollLeft > viewportWidth;

    if (isOffscreen) {
      return this.hiddenMeasurement(object);
    }

    return {
      visible: true,
      overlayVisible: this.enabled && entryEnabled,
      badgeVisible: this.enabled,
      inview: object.getProperty<boolean>("is-inview") === true,
      boxDocX: contentX,
      boxDocY: contentY,
      width,
      height,
      borderRadius,
      topZoneDocX: contentX,
      topZoneDocY: topZoneStart,
      topZoneWidth: width,
      topZoneHeight: Math.max(topZoneHeight, 1),
      topZoneEmpty: topZoneHeight === 0,
      bottomZoneDocX: contentX,
      bottomZoneDocY: bottomZoneStart,
      bottomZoneWidth: width,
      bottomZoneHeight: Math.max(bottomZoneHeight, 1),
      bottomZoneEmpty: bottomZoneHeight === 0,
      topMarkerDocX: markerDocX,
      topMarkerDocY,
      topMarkerValue: formatOffset(topTrigger - topAnchor),
      bottomMarkerDocX: markerDocX,
      bottomMarkerDocY,
      bottomMarkerValue: formatOffset(bottomTrigger - bottomAnchor),
      badgeDocX,
      badgeDocY,
    };
  }

  protected applyMeasurement(entry: OffsetOverlayEntry, measurement: OffsetOverlayMeasurement | undefined): void {
    if (!measurement) {
      entry.root.setAttribute("data-visible", "false");
      entry.topMarker.setAttribute("data-visible", "false");
      entry.bottomMarker.setAttribute("data-visible", "false");
      entry.topMarkerValue.textContent = "";
      entry.bottomMarkerValue.textContent = "";
      entry.topMarker.setAttribute("hidden", "");
      entry.bottomMarker.setAttribute("hidden", "");
      entry.badge.setAttribute("hidden", "");
      return;
    }

    entry.root.setAttribute(
      "data-visible",
      measurement.visible && measurement.overlayVisible ? "true" : "false",
    );
    entry.root.setAttribute("data-inview", measurement.inview ? "true" : "false");
    entry.badge.setAttribute("data-active", measurement.overlayVisible ? "true" : "false");
    entry.badge.title = measurement.overlayVisible
      ? "Hide offsets for this element"
      : "Show offsets for this element";

    if (!measurement.visible || !measurement.badgeVisible) {
      entry.topMarker.setAttribute("data-visible", "false");
      entry.bottomMarker.setAttribute("data-visible", "false");
      entry.topMarkerValue.textContent = "";
      entry.bottomMarkerValue.textContent = "";
      entry.topMarker.setAttribute("hidden", "");
      entry.bottomMarker.setAttribute("hidden", "");
      entry.badge.setAttribute("hidden", "");
      return;
    }

    entry.topMarker.removeAttribute("hidden");
    entry.bottomMarker.removeAttribute("hidden");
    entry.badge.removeAttribute("hidden");
    entry.topMarker.setAttribute("data-visible", "true");
    entry.bottomMarker.setAttribute("data-visible", "true");

    if (!measurement.overlayVisible) {
      entry.topMarker.setAttribute("data-visible", "false");
      entry.bottomMarker.setAttribute("data-visible", "false");
      entry.topMarker.setAttribute("hidden", "");
      entry.bottomMarker.setAttribute("hidden", "");
    }

    styleTxn.setProps(entry.box, {
      width: `${measurement.width}px`,
      height: `${measurement.height}px`,
      borderRadius: measurement.borderRadius,
      transform: fixedTranslate(measurement.boxDocX, measurement.boxDocY),
    });

    styleTxn.setProps(entry.topZone, {
      width: `${measurement.topZoneWidth}px`,
      height: `${measurement.topZoneHeight}px`,
      transform: fixedTranslate(measurement.topZoneDocX, measurement.topZoneDocY),
    });
    entry.topZone.setAttribute("data-empty", measurement.topZoneEmpty ? "true" : "false");

    styleTxn.setProps(entry.bottomZone, {
      width: `${measurement.bottomZoneWidth}px`,
      height: `${measurement.bottomZoneHeight}px`,
      transform: fixedTranslate(measurement.bottomZoneDocX, measurement.bottomZoneDocY),
    });
    entry.bottomZone.setAttribute("data-empty", measurement.bottomZoneEmpty ? "true" : "false");

    if (measurement.overlayVisible) {
      entry.topMarkerValue.textContent = measurement.topMarkerValue;
      styleTxn.setProps(entry.topMarker, {
        transform: `${fixedTranslate(measurement.topMarkerDocX, measurement.topMarkerDocY)} translate(-100%, -116%)`,
      });
      entry.topMarker.setAttribute("data-offset-value", measurement.topMarkerValue);
      entry.topMarker.setAttribute("data-empty", measurement.topZoneEmpty ? "true" : "false");
      entry.topMarker.title = `Inview enter offset: ${measurement.topMarkerValue}`;

      entry.bottomMarkerValue.textContent = measurement.bottomMarkerValue;
      styleTxn.setProps(entry.bottomMarker, {
        transform: `${fixedTranslate(measurement.bottomMarkerDocX, measurement.bottomMarkerDocY)} translate(-100%, 16%)`,
      });
      entry.bottomMarker.setAttribute("data-offset-value", measurement.bottomMarkerValue);
      entry.bottomMarker.setAttribute("data-empty", measurement.bottomZoneEmpty ? "true" : "false");
      entry.bottomMarker.title = `Inview exit offset: ${measurement.bottomMarkerValue}`;
    } else {
      entry.topMarkerValue.textContent = "";
      entry.bottomMarkerValue.textContent = "";
    }

    styleTxn.setProps(entry.badge, {
      transform: fixedTranslate(measurement.badgeDocX, measurement.badgeDocY),
    });
  }

  private hiddenMeasurement(object: StringObject): OffsetOverlayMeasurement {
    return {
      visible: false,
      overlayVisible: this.enabled && this.isEntryEnabled(object.id),
      badgeVisible: this.enabled,
      inview: object.getProperty<boolean>("is-inview") === true,
      boxDocX: 0,
      boxDocY: 0,
      width: 0,
      height: 0,
      borderRadius: "0px",
      topZoneDocX: 0,
      topZoneDocY: 0,
      topZoneWidth: 0,
      topZoneHeight: 1,
      topZoneEmpty: true,
      bottomZoneDocX: 0,
      bottomZoneDocY: 0,
      bottomZoneWidth: 0,
      bottomZoneHeight: 1,
      bottomZoneEmpty: true,
      topMarkerDocX: 0,
      topMarkerDocY: 0,
      topMarkerValue: "0",
      bottomMarkerDocX: 0,
      bottomMarkerDocY: 0,
      bottomMarkerValue: "0",
      badgeDocX: 0,
      badgeDocY: 0,
    };
  }

  private isEntryEnabled(objectId: string): boolean {
    return this.entryEnabled.get(objectId) !== false;
  }

  private setEntryEnabled(objectId: string, enabled: boolean): void {
    this.entryEnabled.set(objectId, enabled);
    if (!this.enabled) return;
    this.scheduleFullSync();
  }

  private getViewportAnchorOffset(anchor: string): number {
    if (anchor === "bottom" || anchor === "right") {
      if (anchor === "right") {
        return this.data.viewport.windowWidth || window.innerWidth;
      }
      return this.data.viewport.windowHeight || window.innerHeight;
    }
    return 0;
  }

}

export { StringDevOffsets as StringOffsets };
